/**
 * 3) « Mon abonnement » (SPEC MC1, MC4 ; retours M3, M7 ; retour de Daphné du 07/10) — maquette 17-compte,
 * `subscriptionCard`. Données = Subscription/MySubscription.tsx (GET client/clientcontracts ; catalogue GET
 * sale/contracts pour le montant de repli, chargé seulement s'il sert : needsCatalogue). Masqué sans abonnement
 * réellement actif (lib/subscription.ts isActiveSubscription : ni terminé, ni résilié, ni à 0 €). AUCUN bouton de
 * pause ni de résiliation.
 */
import { useListClientContracts } from 'hooks/useApiQueries';
import { useContractCatalogue } from '../hooks';
import { fmtDateExpiry, fmtEuros, toDate } from '../lib/format';
import {
  activeContracts,
  countRemainingEvents,
  needsCatalogue,
  nextEventDate,
  resolveContractAmount,
  subscriptionStatus,
} from '../lib/subscription';
import { SectionHead, Tag } from './Ui';

const dateOrDash = (iso: string | null | undefined) => {
  const d = toDate(iso);
  return d ? fmtDateExpiry(d) : '—';
};

export function Subscription() {
  const { data: contracts, isLoading } = useListClientContracts();
  const withCatalogue = needsCatalogue(contracts);
  const { data: catalogue, isLoading: catalogueLoading } = useContractCatalogue(withCatalogue);
  const active = activeContracts(contracts, catalogue);

  // = MySubscription : rien tant qu'on ne sait pas, rien sans abonnement actif.
  if (isLoading || (withCatalogue && catalogueLoading) || active.length === 0) return null;

  return (
    <section className="mmv2-acct-section" id="mon-abonnement" aria-labelledby="mmv2-acct-subscription">
      <SectionHead id="mmv2-acct-subscription" title="Mon abonnement" />
      <div className="mmv2-acct-stack">
        {active.map((contract) => {
          const status = subscriptionStatus(contract.AutopayStatus);
          const { label, amount } = resolveContractAmount(
            contract,
            catalogue?.find((c) => c.Id === contract.ContractID),
          );
          const remaining = countRemainingEvents(contract.UpcomingAutopayEvents);
          return (
            <div key={contract.Id} className="mmv2-card mmv2-card--pad mmv2-acct-sub">
              <div className="mmv2-acct-spread">
                <h3 className="mmv2-acct-sub__name">{contract.ContractName}</h3>
                <Tag tone={status.tone}>{status.label}</Tag>
              </div>
              <dl className="mmv2-acct-sub__grid">
                <div>
                  <dt>{label}</dt>
                  <dd className="mmv2-acct-sub__amount">{fmtEuros(amount)}</dd>
                </div>
                <div>
                  <dt>Prochain paiement</dt>
                  <dd>{dateOrDash(nextEventDate(contract.UpcomingAutopayEvents))}</dd>
                </div>
                {contract.TerminationDate ? (
                  <div>
                    <dt>Résilié, fin le</dt>
                    <dd>{dateOrDash(contract.TerminationDate)}</dd>
                  </div>
                ) : null}
                {contract.MinimumCommitmentEndDate ? (
                  <div>
                    <dt>Fin d’engagement</dt>
                    <dd>{dateOrDash(contract.MinimumCommitmentEndDate)}</dd>
                  </div>
                ) : null}
                {remaining > 0 ? (
                  <div>
                    <dt>Paiements restants</dt>
                    <dd>{remaining}</dd>
                  </div>
                ) : null}
              </dl>
              <p className="mmv2-acct-sub__since mmv2-tiny mmv2-muted">Depuis le {dateOrDash(contract.StartDate)}</p>
            </div>
          );
        })}
      </div>
    </section>
  );
}
